import { forwardRef, useState, useMemo, type ComponentType } from 'react'

import styles from './PaymentMethodSelector.module.css'
import { CreditCardIcon, PayPalIcon, ApplePayIcon, GooglePayIcon } from '../../icons'

import type {
  PaymentMethodType,
  PaymentMethodOption as CorePaymentMethodOption,
} from '@checkout/core'

export interface PaymentMethodOption extends CorePaymentMethodOption {
  provider?: string
}

export type PaymentMethodIntentReason = 'hover' | 'focus' | 'press'

/** Chevron icon for expand/collapse */
const ChevronIcon = ({ expanded, className }: { expanded: boolean; className?: string }) => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
    style={{
      transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
      transition: 'transform 0.2s ease',
    }}
  >
    <path
      d="M4 6L8 10L12 6"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

/** Selected check badge SVG - triangle with checkmark */
const SelectedBadge = () => (
  <svg
    width="26"
    height="20"
    viewBox="0 0 26 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={styles.checkBadge}
  >
    <path d="M20 19.9998H0L26 0V13.9998C26 17.3135 23.3137 19.9998 20 19.9998Z" fill="#216CE0" />
    <path
      d="M14.5 13.7222L17.3571 16.5L22.5 11.5"
      stroke="white"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

export interface PaymentMethodSelectorProps {
  /** Available payment methods */
  methods: PaymentMethodOption[]
  /** Currently selected method */
  selectedMethod: PaymentMethodType | null
  /** Selection handler */
  onSelect: (method: PaymentMethodType) => void
  /** Called when a user is likely to select a method */
  onIntent?: (method: PaymentMethodOption, reason: PaymentMethodIntentReason) => void
  /** Whether the selector is disabled */
  disabled?: boolean
  /** Layout direction */
  layout?: 'horizontal' | 'vertical'
  /** Whether to show labels (default: true) */
  showLabels?: boolean
  /** Additional class name */
  className?: string
  /** Whether to enable collapsible mode (default: true) */
  collapsible?: boolean
  /** Number of methods to show when collapsed (default: 4) */
  defaultVisibleCount?: number
  /** Text for "Show more" button */
  showMoreText?: string
  /** Text for "Show less" button */
  showLessText?: string
}

/** Fallback icons when no image URL provided */
const FallbackIconMap: Record<string, ComponentType<{ className?: string }>> = {
  card: CreditCardIcon,
  paypal: PayPalIcon,
  apple_pay: ApplePayIcon,
  google_pay: GooglePayIcon,
}

const FULL_BLEED_CARD_ARTWORK_PATH = '/payssion-payment-logos/credit-card.png'

function isFullBleedCardArtworkUrl(icon: string | undefined): boolean {
  if (!icon) {
    return false
  }

  try {
    return new URL(icon).pathname === FULL_BLEED_CARD_ARTWORK_PATH
  } catch {
    return icon === FULL_BLEED_CARD_ARTWORK_PATH
  }
}

export const PaymentMethodSelector = forwardRef<HTMLDivElement, PaymentMethodSelectorProps>(
  (
    {
      methods,
      selectedMethod,
      onSelect,
      onIntent,
      disabled = false,
      layout = 'horizontal',
      showLabels = true,
      className,
      collapsible = true,
      defaultVisibleCount = 4,
      showMoreText = 'More',
      showLessText = 'Less',
    },
    ref
  ) => {
    const [isExpanded, setIsExpanded] = useState(false)

    const availableMethods = useMemo(() => methods.filter((m) => m.available), [methods])

    // Determine if we need to show the expand/collapse button
    const shouldCollapse = collapsible && availableMethods.length > defaultVisibleCount

    // Get visible methods based on expanded state
    const visibleMethods = useMemo(() => {
      if (!shouldCollapse || isExpanded) {
        return availableMethods
      }
      return availableMethods.slice(0, defaultVisibleCount)
    }, [availableMethods, shouldCollapse, isExpanded, defaultVisibleCount])

    // Count of hidden methods
    const hiddenCount = availableMethods.length - defaultVisibleCount

    if (availableMethods.length === 0) {
      return (
        <div ref={ref} className={`${styles.container} ${className ?? ''}`}>
          <p className={styles.noMethods}>No payment methods available</p>
        </div>
      )
    }

    const containerClass = layout === 'horizontal' ? styles.horizontal : styles.vertical

    const usesFullBleedCardArtwork = (method: PaymentMethodOption) =>
      isFullBleedCardArtworkUrl(method.icon)

    const hasRenderableIcon = (method: PaymentMethodOption) =>
      Boolean(method.icon || FallbackIconMap[method.pmId])

    const getAnalyticsMethodKey = (method: PaymentMethodOption) => {
      const providerKey = (method.provider || 'unknown').replace(/[^a-z0-9_]/gi, '_').toLowerCase()
      const methodKey = method.pmId.replace(/[^a-z0-9_]/gi, '_').toLowerCase()
      return `${providerKey}_${methodKey}`
    }

    const renderIcon = (method: PaymentMethodOption) => {
      const useCardArtwork = usesFullBleedCardArtwork(method)
      const iconClass = useCardArtwork ? styles.iconFill : styles.brandIcon

      // Use backend image URL if provided
      if (method.icon) {
        return <img src={method.icon} alt={method.label} className={iconClass} />
      }
      // Fall back to built-in icon
      const FallbackIcon = FallbackIconMap[method.pmId]
      if (FallbackIcon) {
        return <FallbackIcon className={iconClass} />
      }
      return null
    }

    const handleToggleExpand = () => {
      setIsExpanded((prev) => !prev)
    }

    return (
      <div
        ref={ref}
        className={`${styles.container} ${containerClass} ${className ?? ''}`}
        role="radiogroup"
        aria-label="Payment methods"
      >
        {visibleMethods.map((method) => {
          const isSelected = selectedMethod === method.type
          const useCardArtwork = usesFullBleedCardArtwork(method)
          const hasIcon = hasRenderableIcon(method)
          const shouldRenderLabel = !useCardArtwork && (showLabels || !hasIcon)

          return (
            <button
              key={method.type}
              type="button"
              role="radio"
              aria-checked={isSelected}
              aria-label={method.label}
              className={`${styles.methodButton} ${isSelected ? styles.selected : ''} ${useCardArtwork ? styles.noPadding : ''}`}
              onClick={() => onSelect(method.type)}
              onMouseEnter={() => onIntent?.(method, 'hover')}
              onFocus={() => onIntent?.(method, 'focus')}
              onPointerDown={() => onIntent?.(method, 'press')}
              disabled={disabled}
              data-ga-action-name={`payment_method_select_${getAnalyticsMethodKey(method)}`}
              data-ga-module="payment_methods"
              data-payment-provider={method.provider || 'unknown'}
              data-payment-method-id={method.pmId}
              data-payment-selection-id={method.type}
            >
              {layout === 'horizontal' ? (
                <>
                  {shouldRenderLabel && <span className={styles.label}>{method.label}</span>}
                  {hasIcon && (
                    <div className={useCardArtwork ? styles.iconWrapperFill : styles.iconWrapper}>
                      {renderIcon(method)}
                    </div>
                  )}
                </>
              ) : (
                <>
                  {hasIcon && (
                    <div className={useCardArtwork ? styles.iconWrapperFill : styles.iconWrapper}>
                      {renderIcon(method)}
                    </div>
                  )}
                  {shouldRenderLabel && <span className={styles.label}>{method.label}</span>}
                </>
              )}
              {isSelected && <SelectedBadge />}
            </button>
          )
        })}

        {/* Expand/Collapse button */}
        {shouldCollapse && (
          <button
            type="button"
            className={styles.moreButton}
            onClick={handleToggleExpand}
            aria-expanded={isExpanded}
            aria-label={isExpanded ? showLessText : `${showMoreText} (+${hiddenCount})`}
            data-ga-action-name={isExpanded ? 'payment_methods_collapse' : 'payment_methods_expand'}
            data-ga-module="payment_methods"
          >
            <span>{isExpanded ? showLessText : `${showMoreText} (+${hiddenCount})`}</span>
            <ChevronIcon expanded={isExpanded} className={styles.chevron} />
          </button>
        )}
      </div>
    )
  }
)

PaymentMethodSelector.displayName = 'PaymentMethodSelector'
